import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { fmtDateIST, inr } from "@/lib/dates";
import { toast } from "sonner";
import { Download, RotateCcw, Search } from "lucide-react";
import api, { apiError } from "@/lib/api";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, PackageCheck, Truck, Undo2, Zap, ListOrdered } from "lucide-react";

const STATUSES = ["Picked Up", "In Transit", "Delivered", "Returned to Client"];
const TYPES = ["Normal", "Express"];

function exportCSV(orders) {
  const header = ["Order ID", "Client", "BDO / Ref No.", "Passenger Name", "Passenger Number", "Bags", "Delivery Location", "Delivery Type", "Status", "Created By", "Created", "Updated", "Amount", "GST (18%)", "Total (incl. GST)"];
  const rows = orders.map((o) => [
    o.order_id, o.client_name, o.bdo_reference_no || "", o.passenger_name, o.passenger_number,
    o.number_of_bags, o.delivery_location, o.delivery_type, o.delivery_status, o.created_by_name,
    format(new Date(o.created_at), "yyyy-MM-dd HH:mm"), format(new Date(o.updated_at), "yyyy-MM-dd HH:mm"),
    o.amount ?? "", o.gst_amount ?? "", o.total_amount ?? "",
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ezship-orders-report-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [clientId, setClientId] = useState("all");
  const [status, setStatus] = useState("all");
  const [dtype, setDtype] = useState("all");
  const [staffId, setStaffId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/clients/options").then((r) => setClients(r.data)).catch(() => {});
    api.get("/staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (clientId !== "all") p.set("client_id", clientId);
      if (status !== "all") p.set("status", status);
      if (dtype !== "all") p.set("delivery_type", dtype);
      if (staffId !== "all") p.set("staff_id", staffId);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const { data } = await api.get(`/reports/summary?${p.toString()}`);
      setResult(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [clientId, status, dtype, staffId, dateFrom, dateTo]);

  useEffect(() => { run(); }, [run]);

  const clear = () => { setClientId("all"); setStatus("all"); setDtype("all"); setStaffId("all"); setDateFrom(""); setDateTo(""); };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Generate filtered order reports and export to CSV.</p>
        </div>
        <Button
          onClick={() => result && exportCSV(result.orders)}
          disabled={!result || result.orders.length === 0}
          data-testid="export-csv-button"
          className="bg-primary hover:bg-[#152D53] rounded-sm"
        >
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="report-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="report-filter-client" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="report-filter-status" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dtype} onValueChange={setDtype}>
            <SelectTrigger data-testid="report-filter-type" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={staffId} onValueChange={setStaffId}>
            <SelectTrigger data-testid="report-filter-staff" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Staff" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">From date</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" className="h-10 border-slate-300 rounded-sm" placeholder="From date" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">To date</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" className="h-10 border-slate-300 rounded-sm" placeholder="To date" />
          </div>
          <div className="flex gap-2">
            <Button onClick={run} data-testid="report-apply-button" className="flex-1 h-10 bg-primary hover:bg-[#152D53] rounded-sm">
              <Search className="h-4 w-4 mr-1" /> Apply
            </Button>
            <Button variant="outline" onClick={clear} data-testid="report-clear-button" className="h-10 rounded-sm border-slate-300">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="report-totals">
          <StatCard label="Total" value={result.totals.total} icon={ListOrdered} tone="navy" testId="report-total" />
          <StatCard label="Normal" value={result.totals.normal} icon={Package} tone="slate" testId="report-normal" />
          <StatCard label="Express" value={result.totals.express} icon={Zap} tone="red" testId="report-express" />
          <StatCard label="Picked Up" value={result.totals.picked_up} icon={Package} tone="blue" testId="report-picked-up" />
          <StatCard label="In Transit" value={result.totals.in_transit} icon={Truck} tone="orange" testId="report-in-transit" />
          <StatCard label="Delivered" value={result.totals.delivered} icon={PackageCheck} tone="green" testId="report-delivered" />
          <StatCard label="Returned" value={result.totals.returned} icon={Undo2} tone="red" testId="report-returned" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md" data-testid="report-table-card">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-sm animate-pulse" />)}</div>
        ) : !result || result.orders.length === 0 ? (
          <EmptyState title="No orders match these filters" testId="report-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Order ID", "Client", "Passenger", "Type", "Status", "Created By", "Created"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 even:bg-slate-50/60" data-testid={`report-row-${o.order_id}`}>
                    <td className="px-4 py-2.5 font-semibold text-primary whitespace-nowrap">{o.order_id}</td>
                    <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{o.client_name}</td>
                    <td className="px-4 py-2.5 text-slate-800 whitespace-nowrap">{o.passenger_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{o.delivery_type}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={o.delivery_status} /></td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{o.created_by_name}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs tnum whitespace-nowrap">{fmtDateIST(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
